// Client-scoped email-draft API (TASK-496 EmailDraft). Mirrors the SixEase api/messages.ts
// surface, wired to the Kielis meeting-orchestrator drafts endpoints (migration 168 backing
// store). `provenance[]` are the Locked Facts the draft was grounded on.
import { httpClient } from '../../lib/httpClient';

export interface ProvenanceEntry {
  fact_key: string;
  value: string;
  source: string;
}

export interface MessageDraft {
  id: string;
  client_id: string;
  session_id: string | null;
  channel: string | null;
  subject: string | null;
  draft_text: string | null;
  provenance: ProvenanceEntry[];
  style: string | null;
  status: string;
  // TASK-656: fact-scope verdict — 'unverified' blocks Approve/Send in the UI
  // and is enforced server-side (409). 'unchecked' = pre-guard legacy rows.
  scope_status?: 'unchecked' | 'verified' | 'unverified';
  scope_note?: string | null;
  sent_at?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SendTestResponse {
  status: string;
  mailhog_ui: string;
}

export interface GenerateDraftBody {
  session_id?: string;
  recipient_email?: string;
  recipient_name?: string;
  style?: string;
  /** The language to compose in. Absent = the server's default (de). */
  language?: string;
  /**
   * What the email is FOR. `information_request` is grounded on the client's own open KYC
   * gaps and needs no history; `follow_up` needs something to follow up on and is refused
   * (422) when the client has no facts, no meeting and no topic.
   */
  purpose?: 'follow_up' | 'information_request';
  /**
   * TASK-824: what the email is ABOUT. The route appends "Focus of this email: {topic}" to
   * its DNA-grounded summary, and refuses (422) a follow-up that has neither grounded facts
   * nor a topic — so this is grounding, not decoration. It is how a draft composed from a
   * signal stays about that signal instead of being the client's most recent unrelated letter.
   */
  topic?: string;
}

export const draftApiService = {
  getLatestDraft(clientId: string) {
    return httpClient.get<MessageDraft>(`/api/meeting/drafts/client/${clientId}/latest`);
  },
  /** One specific draft — a task artifact points at an id, not at "the latest" (TASK-702). */
  getDraft(draftId: string) {
    return httpClient.get<MessageDraft>(`/api/meeting/drafts/${draftId}`);
  },
  generateDraft(clientId: string, body: GenerateDraftBody) {
    return httpClient.post<MessageDraft>(`/api/meeting/drafts/client/${clientId}/generate`, body);
  },
  patchDraftText(draftId: string, body: { subject?: string; draft_text?: string }) {
    return httpClient.patch<MessageDraft>(`/api/meeting/drafts/${draftId}`, body);
  },
  approveDraft(draftId: string) {
    return httpClient.post<MessageDraft>(`/api/meeting/drafts/${draftId}/approve`, {});
  },
  sendTestDraft(draftId: string) {
    return httpClient.post<SendTestResponse>(`/api/meeting/drafts/${draftId}/send-test`, {});
  },
};
